import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import * as Y from "yjs"
import { Pencil, Eraser, Trash2, Circle, Undo2, Redo2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { useClassroomDoc } from "./LiveDocProvider"
import {
  type Stroke,
  type StrokeTool,
  type NormPoint,
  drawStroke,
  drawBrushCursor,
  makeStrokeId,
  nextStrokeCounter,
  toNormalized,
  downsamplePoints,
  strokesTouchedBySegment,
  eraseAreaFromStroke,
  strokeLineWidth,
  LINE_ERASER_HIT_RADIUS_PX,
} from "@/lib/live/strokes"

// Keyed, not named: the accessible name of each swatch is translated
// (liveSession.canvas.colors.*). These were English in every locale.
const COLORS = [
  { key: "black", value: "#1a1a2e" },
  { key: "red", value: "#ef4444" },
  { key: "blue", value: "#3b82f6" },
  { key: "green", value: "#22c55e" },
  { key: "orange", value: "#f97316" },
] as const

// Cap the points streamed in the live (awareness) preview — the committed stroke
// keeps full fidelity; the in-progress preview only needs to look right.
const PREVIEW_MAX_POINTS = 200

// The eraser's two modes (#942): "line" removes a whole touched line, "area" is
// the classic rub-out — which now really deletes ink (#1022) instead of
// committing an invisible destination-out stroke on top.
type EraserMode = "line" | "area"

// Remembered for the rest of the lesson (module scope: the board unmounts when
// its panel closes). Whole-line is the default — it's what users expected.
let lastEraserMode: EraserMode = "line"

// Origin for every local board write. The per-surface Y.UndoManager tracks only
// this origin, so undo/redo is per-person: a peer's strokes (applied by the
// sync provider under its own origin) never land on my stack.
const LOCAL_ORIGIN = "collab-canvas-local"

// How long to keep bridging a remote peer's finished-but-not-yet-committed stroke
// before giving up (e.g. the peer left mid-stroke and never committed).
const RETAINED_DRAW_MAX_MS = 8000

interface CursorState {
  surface: string
  x: number
  y: number
}
interface DrawState {
  surface: string
  stroke: Stroke
}
interface UserState {
  name?: string
  color?: string
}

interface CollabCanvasProps {
  // Distinguishes independent drawing surfaces that share one doc/awareness
  // (e.g. "whiteboard" vs "annotation"). Strokes, cursors and in-progress
  // previews are scoped to a surface.
  surface: string
  // Transparent overlay (annotation over screen-share) vs a white board.
  transparent?: boolean
  showToolbar?: boolean
  className?: string
}

// A real-time collaborative drawing surface backed by a Yjs array (committed
// strokes) + awareness (live cursors and in-progress strokes). Used by both the
// whiteboard and the screen-share annotation overlay.
export function CollabCanvas({
  surface,
  transparent = false,
  showToolbar = true,
  className,
}: CollabCanvasProps) {
  const { doc, awareness } = useClassroomDoc()
  const strokesRef = useRef(doc.getArray<Stroke>(`wb:${surface}`))

  const [tool, setTool] = useState<StrokeTool>("pen")
  const { t } = useTranslation()
  const [color, setColor] = useState<string>(COLORS[0].value)
  const [brushSize, setBrushSize] = useState(3)
  const [eraserMode, setEraserModeState] = useState<EraserMode>(lastEraserMode)
  const setEraserMode = (mode: EraserMode) => {
    lastEraserMode = mode
    setEraserModeState(mode)
  }
  const [history, setHistory] = useState({ undo: false, redo: false })

  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const committedRef = useRef<HTMLCanvasElement | null>(null)
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 })

  const drawingRef = useRef(false)
  const currentStrokeRef = useRef<Stroke | null>(null)
  // The eraser drag in progress (either mode): it never creates a stroke, so it
  // has state of its own — the previous point, to erase segment by segment.
  const erasingRef = useRef(false)
  const lastErasePointRef = useRef<NormPoint | null>(null)
  // The one pointer a gesture belongs to. Without it, a second finger (or a
  // palm) would interleave its moves and a "previous point -> this point"
  // eraser segment would sweep across the board between the two contact points.
  const activePointerRef = useRef<number | null>(null)
  const undoManagerRef = useRef<Y.UndoManager | null>(null)

  // Pending ephemeral state flushed to awareness once per animation frame.
  const cursorRef = useRef<CursorState | null>(null)
  const drawRef = useRef<DrawState | null>(null)
  const cursorDirtyRef = useRef(false)
  const drawDirtyRef = useRef(false)
  const rafRef = useRef<number | null>(null)
  // Whether our pointer is one that hovers (mouse, stylus). Only then is our own
  // brush circle drawn at cursorRef: a finger has no hover. Local, never sent.
  const hoverPointerRef = useRef(false)
  // Last in-progress stroke seen from each remote client, retained to bridge the
  // gap between their live preview clearing and their committed stroke arriving
  // over the lossy data channel (the committed stroke is multi-chunk; the
  // awareness draw-clear is a single tiny message that can land first).
  const lastRemoteDrawRef = useRef<Map<number, { stroke: Stroke; at: number }>>(new Map())

  // Mirror toolbar settings into refs so pointer handlers always read the latest
  // without re-binding listeners.
  const toolRef = useRef(tool)
  const colorRef = useRef(color)
  const brushRef = useRef(brushSize)
  const eraserModeRef = useRef(eraserMode)
  toolRef.current = tool
  colorRef.current = color
  brushRef.current = brushSize
  eraserModeRef.current = eraserMode

  const renderCommitted = useCallback(() => {
    const committed = committedRef.current
    if (!committed) return
    const ctx = committed.getContext("2d")
    if (!ctx) return
    const { w, h, dpr } = sizeRef.current
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)
    for (const stroke of strokesRef.current.toArray()) {
      drawStroke(ctx, stroke, w, h)
    }
  }, [])

  const drawCursor = useCallback(
    (ctx: CanvasRenderingContext2D, x: number, y: number, user: UserState | undefined) => {
      const color = user?.color ?? "#3b82f6"
      const name = user?.name ?? ""
      ctx.save()
      ctx.globalCompositeOperation = "source-over"
      ctx.beginPath()
      ctx.arc(x, y, 4, 0, Math.PI * 2)
      ctx.fillStyle = color
      ctx.fill()
      if (name) {
        ctx.font = "11px ui-sans-serif, system-ui, sans-serif"
        const textWidth = ctx.measureText(name).width
        const bx = x + 8
        const by = y + 8
        ctx.fillStyle = color
        ctx.fillRect(bx, by, textWidth + 8, 16)
        ctx.fillStyle = "#ffffff"
        ctx.fillText(name, bx + 4, by + 12)
      }
      ctx.restore()
    },
    [],
  )

  const renderOverlay = useCallback(() => {
    const canvas = canvasRef.current
    const committed = committedRef.current
    if (!canvas || !committed) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    const { w, h, dpr } = sizeRef.current
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)
    ctx.drawImage(committed, 0, 0, w, h)

    if (drawingRef.current && currentStrokeRef.current) {
      drawStroke(ctx, currentStrokeRef.current, w, h)
    }

    const states = awareness.getStates()
    const committedIds = new Set(strokesRef.current.toArray().map((s) => s.id))
    const drawingNow = new Set<number>()
    states.forEach((state, clientId) => {
      if (clientId === doc.clientID) return
      const draw = state.draw as DrawState | undefined
      if (draw && draw.surface === surface && draw.stroke) {
        drawingNow.add(clientId)
        lastRemoteDrawRef.current.set(clientId, { stroke: draw.stroke, at: Date.now() })
        drawStroke(ctx, draw.stroke, w, h)
      }
    })
    // Bridge the preview->committed handoff: a peer whose live draw just cleared
    // but whose committed stroke hasn't landed yet (lossy/reordered channel) —
    // keep drawing its last preview so the finished stroke doesn't flicker out.
    // Drop it once the committed stroke arrives (or after a safety timeout).
    const now = Date.now()
    lastRemoteDrawRef.current.forEach((entry, clientId) => {
      if (drawingNow.has(clientId)) return
      if (committedIds.has(entry.stroke.id) || now - entry.at > RETAINED_DRAW_MAX_MS) {
        lastRemoteDrawRef.current.delete(clientId)
        return
      }
      drawStroke(ctx, entry.stroke, w, h)
    })
    states.forEach((state, clientId) => {
      if (clientId === doc.clientID) return
      const cursor = state.cursor as CursorState | undefined
      if (cursor && cursor.surface === surface) {
        drawCursor(ctx, cursor.x * w, cursor.y * h, state.user as UserState | undefined)
      }
    })

    // Our own cursor: a circle the size of the brush, in place of the browser
    // cursor (#951). Drawn on this screen only — the other participant keeps
    // seeing the awareness dot above. The browser cursor is hidden only while
    // the circle shows, so a board that opens under a resting mouse (or before
    // hydration) still has its crosshair. React renders no `style` on the
    // board, so it never undoes this.
    // The whole-line eraser has no size (its hit target never changes), so it
    // keeps the browser's normal cursor instead of a size circle (#942/#951).
    const fixedCursor = toolRef.current === "eraser" && eraserModeRef.current === "line"
    const local = cursorRef.current
    const showBrush = hoverPointerRef.current && local !== null && !fixedCursor
    if (containerRef.current) containerRef.current.style.cursor = showBrush ? "none" : ""
    if (showBrush) {
      drawBrushCursor(
        ctx,
        { tool: toolRef.current, size: brushRef.current, color: colorRef.current },
        local.x * w,
        local.y * h,
        w,
      )
    }
  }, [awareness, doc, surface, drawCursor])

  const flushAwareness = useCallback(() => {
    if (cursorDirtyRef.current) {
      awareness.setLocalStateField("cursor", cursorRef.current)
      cursorDirtyRef.current = false
    }
    if (drawDirtyRef.current) {
      awareness.setLocalStateField("draw", drawRef.current)
      drawDirtyRef.current = false
    }
  }, [awareness])

  const scheduleOverlay = useCallback(() => {
    if (rafRef.current !== null) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      flushAwareness()
      renderOverlay()
    })
  }, [flushAwareness, renderOverlay])

  // Wire sizing, the committed cache, and the doc/awareness observers once.
  useEffect(() => {
    const container = containerRef.current
    const canvas = canvasRef.current
    const strokes = strokesRef.current
    if (!container || !canvas) return
    if (!committedRef.current) committedRef.current = document.createElement("canvas")

    const resize = () => {
      const rect = container.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return
      const dpr = window.devicePixelRatio || 1
      sizeRef.current = { w: rect.width, h: rect.height, dpr }
      for (const c of [canvas, committedRef.current as HTMLCanvasElement]) {
        c.width = Math.round(rect.width * dpr)
        c.height = Math.round(rect.height * dpr)
      }
      renderCommitted()
      scheduleOverlay()
    }
    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(container)

    const onStrokes = () => {
      renderCommitted()
      scheduleOverlay()
    }
    strokes.observe(onStrokes)
    const onAwareness = () => scheduleOverlay()
    awareness.on("change", onAwareness)

    return () => {
      observer.disconnect()
      strokes.unobserve(onStrokes)
      awareness.off("change", onAwareness)
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      // Drop our presence on this surface (leave the other surface untouched).
      const local = awareness.getLocalState()
      if ((local?.cursor as CursorState | undefined)?.surface === surface) {
        awareness.setLocalStateField("cursor", null)
      }
      if ((local?.draw as DrawState | undefined)?.surface === surface) {
        awareness.setLocalStateField("draw", null)
      }
    }
  }, [awareness, surface, renderCommitted, renderOverlay, scheduleOverlay])

  // Per-person undo/redo (#1021) over this surface's strokes: only local
  // transactions (LOCAL_ORIGIN) are tracked, so undoing never rolls back the
  // other participant's work. Lives per mount — closing the board drops its
  // history, the board itself lives on in the doc.
  useEffect(() => {
    const manager = new Y.UndoManager(strokesRef.current, {
      trackedOrigins: new Set([LOCAL_ORIGIN]),
    })
    undoManagerRef.current = manager
    const refresh = () => setHistory({ undo: manager.canUndo(), redo: manager.canRedo() })
    manager.on("stack-item-added", refresh)
    manager.on("stack-item-popped", refresh)
    manager.on("stack-cleared", refresh)
    return () => {
      undoManagerRef.current = null
      manager.destroy()
    }
  }, [])

  // A toolbar change can land while the pointer rests on the board (the size
  // slider moved from the keyboard): repaint so the brush circle follows. Only
  // dirty awareness fields flush, so this sends nothing.
  useEffect(() => {
    scheduleOverlay()
  }, [tool, color, brushSize, eraserMode, scheduleOverlay])

  const pointFromEvent = (e: React.PointerEvent): NormPoint => {
    const rect = (canvasRef.current as HTMLCanvasElement).getBoundingClientRect()
    return toNormalized(e.clientX, e.clientY, rect)
  }

  // Erase whatever the eraser's path segment [from, to] covers, straight in the
  // shared array — "touch it and it disappears", during the drag, not on
  // release. Deletion is by id looked up at delete time inside one transaction:
  // the board is shared live, so a remembered position could point at the wrong
  // stroke by the time we delete (#942).
  const eraseSegment = (from: NormPoint, to: NormPoint) => {
    const { w, h } = sizeRef.current
    if (w === 0 || h === 0) return
    const strokes = strokesRef.current
    if (eraserModeRef.current === "line") {
      const ids = strokesTouchedBySegment(strokes.toArray(), from, to, LINE_ERASER_HIT_RADIUS_PX, w, h)
      if (ids.length === 0) return
      doc.transact(() => {
        for (const id of ids) {
          const index = strokes.toArray().findIndex((s) => s.id === id)
          if (index !== -1) strokes.delete(index, 1)
        }
      }, LOCAL_ORIGIN)
      return
    }
    // Area mode (#1022): really remove the rubbed ink. Touched strokes are
    // split into surviving fragments, replaced at their own position so their
    // z-order (under any legacy rub-out strokes) is preserved.
    const radius = strokeLineWidth({ size: brushRef.current, tool: "eraser" }, w) / 2
    const touched: Array<{ stroke: Stroke; runs: NormPoint[][] }> = []
    for (const stroke of strokes.toArray()) {
      const runs = eraseAreaFromStroke(stroke, from, to, radius, w, h)
      if (runs) touched.push({ stroke, runs })
    }
    if (touched.length === 0) return
    doc.transact(() => {
      for (const { stroke, runs } of touched) {
        const index = strokes.toArray().findIndex((s) => s.id === stroke.id)
        if (index === -1) continue
        strokes.delete(index, 1)
        if (runs.length > 0) {
          strokes.insert(
            index,
            runs.map((points) => ({
              id: makeStrokeId(doc.clientID, nextStrokeCounter()),
              color: stroke.color,
              size: stroke.size,
              tool: "pen" as const,
              points,
            })),
          )
        }
      }
    }, LOCAL_ORIGIN)
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    const canvas = canvasRef.current
    if (!canvas) return
    // One gesture, one pointer: a second finger or a palm is ignored outright.
    if (activePointerRef.current !== null) return
    activePointerRef.current = e.pointerId
    const point = pointFromEvent(e)
    hoverPointerRef.current = e.pointerType !== "touch"
    cursorRef.current = { surface, x: point[0], y: point[1] }
    cursorDirtyRef.current = true
    if (toolRef.current === "eraser") {
      // Neither eraser mode creates a stroke any more: erase in place from the
      // first touch, and stream no draw preview — the peer sees the deletions
      // themselves, live.
      erasingRef.current = true
      lastErasePointRef.current = point
      eraseSegment(point, point)
    } else {
      const stroke: Stroke = {
        id: makeStrokeId(doc.clientID, nextStrokeCounter()),
        color: colorRef.current,
        size: brushRef.current,
        tool: toolRef.current,
        points: [point],
      }
      currentStrokeRef.current = stroke
      drawingRef.current = true
      drawRef.current = { surface, stroke }
      drawDirtyRef.current = true
    }
    canvas.setPointerCapture(e.pointerId)
    scheduleOverlay()
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    // While a gesture is running, other pointers' moves are ignored: an eraser
    // segment between two fingers' positions would sweep the board (#942).
    if (activePointerRef.current !== null && e.pointerId !== activePointerRef.current) return
    const point = pointFromEvent(e)
    hoverPointerRef.current = e.pointerType !== "touch"
    cursorRef.current = { surface, x: point[0], y: point[1] }
    cursorDirtyRef.current = true
    if (erasingRef.current && lastErasePointRef.current) {
      eraseSegment(lastErasePointRef.current, point)
      lastErasePointRef.current = point
    } else if (drawingRef.current && currentStrokeRef.current) {
      currentStrokeRef.current.points.push(point)
      drawRef.current = {
        surface,
        stroke: {
          ...currentStrokeRef.current,
          points: downsamplePoints(currentStrokeRef.current.points, PREVIEW_MAX_POINTS),
        },
      }
      drawDirtyRef.current = true
    }
    scheduleOverlay()
  }

  const commitStroke = () => {
    const stroke = currentStrokeRef.current
    if (!drawingRef.current || !stroke) return
    // Push an independent copy — never hand Yjs a reference we still hold.
    doc.transact(() => {
      strokesRef.current.push([
        {
          id: stroke.id,
          color: stroke.color,
          size: stroke.size,
          tool: stroke.tool,
          points: stroke.points.map((p) => [p[0], p[1]] as NormPoint),
        },
      ])
    }, LOCAL_ORIGIN)
    currentStrokeRef.current = null
    drawingRef.current = false
    drawRef.current = null
    drawDirtyRef.current = true
  }

  const handlePointerUp = (e: React.PointerEvent) => {
    if (activePointerRef.current !== null && e.pointerId !== activePointerRef.current) return
    activePointerRef.current = null
    if (erasingRef.current) {
      erasingRef.current = false
      lastErasePointRef.current = null
    } else {
      commitStroke()
    }
    // Close the undo step: the next gesture gets its own, even if it starts
    // within the capture window.
    undoManagerRef.current?.stopCapturing()
    scheduleOverlay()
  }

  const handlePointerLeave = () => {
    if (!drawingRef.current && !erasingRef.current) {
      cursorRef.current = null
      cursorDirtyRef.current = true
      scheduleOverlay()
    }
  }

  const clearBoard = useCallback(() => {
    const strokes = strokesRef.current
    if (strokes.length === 0) return
    doc.transact(() => {
      strokes.delete(0, strokes.length)
    }, LOCAL_ORIGIN)
    undoManagerRef.current?.stopCapturing()
  }, [doc])

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* On one line the toolbar needs ~446px inside its padding; @md is 448.
          Narrower — a board squeezed beside a grown video or a wide chat — it
          wraps on its own width rather than cutting Clear off (#970): the size
          slider and Clear move down together, Clear still on the right, and
          the divider in front of them goes. */}
      {showToolbar && (
        <div className="@container p-2 @md:p-3 border-b border-border bg-card">
          <div className="flex flex-wrap items-center gap-1.5 @md:gap-2">
            {/* Icon-only controls: each carries its name, and the tools their state. */}
            <Button
              variant={tool === "pen" ? "default" : "ghost"}
              size="sm"
              onClick={() => setTool("pen")}
              aria-label={t("liveSession.canvas.pen")}
              aria-pressed={tool === "pen"}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant={tool === "eraser" ? "default" : "ghost"}
              size="sm"
              onClick={() => setTool("eraser")}
              aria-label={t("liveSession.canvas.eraser")}
              aria-pressed={tool === "eraser"}
            >
              <Eraser className="w-4 h-4" />
            </Button>

            {/* Picking the eraser reveals its two modes (#942). Whole-line is
                the default; the last choice is kept for the rest of the lesson. */}
            {tool === "eraser" && (
              <div className="flex items-center gap-1">
                <Button
                  variant={eraserMode === "line" ? "secondary" : "ghost"}
                  size="sm"
                  className="px-2 text-xs"
                  onClick={() => setEraserMode("line")}
                  aria-pressed={eraserMode === "line"}
                >
                  {t("liveSession.canvas.eraseLines")}
                </Button>
                <Button
                  variant={eraserMode === "area" ? "secondary" : "ghost"}
                  size="sm"
                  className="px-2 text-xs"
                  onClick={() => setEraserMode("area")}
                  aria-pressed={eraserMode === "area"}
                >
                  {t("liveSession.canvas.eraseArea")}
                </Button>
              </div>
            )}

            <div className="w-px h-6 bg-border mx-1" />

            {COLORS.map((c) => (
              <button
                key={c.value}
                onClick={() => {
                  setColor(c.value)
                  setTool("pen")
                }}
                className={cn(
                  "p-1 rounded-md transition-all",
                  color === c.value && tool === "pen" && "ring-2 ring-primary ring-offset-1",
                )}
                aria-label={t(`liveSession.canvas.colors.${c.key}`)}
                aria-pressed={color === c.value && tool === "pen"}
              >
                <Circle className="w-5 h-5" fill={c.value} stroke={c.value} />
              </button>
            ))}

            <div className="hidden @md:block w-px h-6 bg-border mx-1" />

            <div className="flex flex-1 items-center gap-2">
              {/* The whole-line eraser has no size — the slider is inert there. */}
              <input
                type="range"
                min="1"
                max="10"
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                disabled={tool === "eraser" && eraserMode === "line"}
                className="w-20 accent-primary disabled:opacity-40"
                aria-label={t("liveSession.canvas.brushSize")}
              />

              <div className="flex-1" />

              <Button
                variant="ghost"
                size="sm"
                onClick={() => undoManagerRef.current?.undo()}
                disabled={!history.undo}
                aria-label={t("liveSession.canvas.undo")}
              >
                <Undo2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => undoManagerRef.current?.redo()}
                disabled={!history.redo}
                aria-label={t("liveSession.canvas.redo")}
              >
                <Redo2 className="w-4 h-4" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={clearBoard}
                className="hover:bg-destructive hover:text-destructive-foreground"
                aria-label={t("liveSession.canvas.clear")}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* The crosshair gives way to a circle the size of the brush once the
          overlay paints one (renderOverlay sets the inline cursor). */}
      <div
        ref={containerRef}
        className={cn("flex-1 cursor-crosshair min-h-0 relative", transparent ? "bg-transparent" : "bg-white")}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerLeave}
          className="absolute inset-0 w-full h-full touch-none"
        />
      </div>
    </div>
  )
}
